/**
 * Pure helper: filter by date (skip when Live), by section, then group by league.
 * Used inside useMemo for scoreboard list performance.
 */

import { LEAGUE_DISPLAY_PRIORITY } from '../../create/constants/create.constants';
import type { GameResult } from '../types/gameResults.types';
import { GAME_STATUSES, SECTION_FILTER_KEYS } from '../constants/gameResults.constants';
import type { SectionFilterKey } from '../constants/gameResults.constants';
import { localCalendarDateKeyFromIso } from '../../helpers/date.helper';
import { getResolvedScoreboardIanaTimeZone } from '../../helpers/scoreboardIntlTimeZone.helper';

const LEAGUE_PRIORITY_LAST = 9999;

function getLeaguePriorityIndex(leagueName: string): number {
  const key = leagueName.trim().toLowerCase();
  const i = LEAGUE_DISPLAY_PRIORITY.indexOf(key);
  return i === -1 ? LEAGUE_PRIORITY_LAST : i;
}

export interface LeagueGroup {
  leagueName: string;
  games: GameResult[];
}

/**
 * Filter results by section (all / live / recent / upcoming).
 * Live: only live. Recent: only completed. Upcoming: only upcoming. All: no status filter.
 */
function filterBySection(games: GameResult[], sectionFilter: SectionFilterKey): GameResult[] {
  if (sectionFilter === SECTION_FILTER_KEYS.ALL) return games;
  if (sectionFilter === SECTION_FILTER_KEYS.LIVE) return games.filter((g) => g.status === GAME_STATUSES.LIVE);
  if (sectionFilter === SECTION_FILTER_KEYS.RECENT) return games.filter((g) => g.status === GAME_STATUSES.COMPLETED);
  if (sectionFilter === SECTION_FILTER_KEYS.UPCOMING) {
    return games.filter(
      (g) => g.status === GAME_STATUSES.UPCOMING || g.status === GAME_STATUSES.LIVE
    );
  }
  return games;
}

/**
 * Filter + group pipeline. When sectionFilter is 'live', date filter is skipped (show all live matches).
 * When skipDateFilter is true (view by league), date filter is skipped so all dates for the selected league are shown.
 * @param dayBucketIanaTimeZone - Same zone as GET /matches/results ?timezone= (from getResolvedScoreboardIanaTimeZone).
 */
export function groupGameResultsByLeague(
  results: GameResult[],
  selectedDate: string,
  sectionFilter: SectionFilterKey,
  skipDateFilter?: boolean,
  dayBucketIanaTimeZone?: string
): LeagueGroup[] {
  const applyDateFilter =
    !skipDateFilter && sectionFilter !== SECTION_FILTER_KEYS.LIVE;
  const tz = dayBucketIanaTimeZone ?? getResolvedScoreboardIanaTimeZone();
  const byDate = applyDateFilter
    ? results.filter((g) => localCalendarDateKeyFromIso(g.date, tz) === selectedDate)
    : results;
  const bySection = filterBySection(byDate, sectionFilter);

  const byLeague = new Map<string, GameResult[]>();
  for (const game of bySection) {
    const name = game.league.trim() || 'Other';
    const list = byLeague.get(name) ?? [];
    list.push(game);
    byLeague.set(name, list);
  }

  const leagues = Array.from(byLeague.entries())
    .map(([leagueName, games]) => ({
      leagueName,
      games: games.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    }))
    .sort((a, b) => getLeaguePriorityIndex(a.leagueName) - getLeaguePriorityIndex(b.leagueName));

  return leagues;
}
